import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, TOKEN_KEY, REFRESH_TOKEN_KEY } from '../config';
import { TokenResponse, ApiErrorDetail } from '../types';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add the token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for handling token refresh and global errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorDetail>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Check if the error is 401, we have an original request, and we haven't retried yet.
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      
      if (refreshToken) {
        try {
          // Attempt to get a new access token using the refresh token.
          // We use a raw axios call here to avoid an infinite loop of interceptors.
          const { data } = await axios.post<TokenResponse>(`${API_BASE_URL}/auth/login/refresh-token`, {
            refresh_token: refreshToken,
          });
          
          // Store the new access token.
          localStorage.setItem(TOKEN_KEY, data.access_token);
          
          // Update the authorization header on the original request.
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
          }
          
          // Retry the original request with the new token.
          return apiClient(originalRequest);

        } catch (refreshError) {
          // If refreshing fails, it's a "hard logout" scenario.
          console.error("Refresh token failed, logging out:", refreshError);
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          // Dispatch a global event for the AuthContext to catch and update the UI.
          window.dispatchEvent(new CustomEvent('auth-error-logout'));
          return Promise.reject(refreshError);
        }
      } else {
        // If there's no refresh token, perform a hard logout immediately.
        console.error("No refresh token available, logging out.");
        localStorage.removeItem(TOKEN_KEY);
        window.dispatchEvent(new CustomEvent('auth-error-logout'));
      }
    }

    // For all other errors, just reject the promise.
    return Promise.reject(error);
  }
);

export default apiClient;